import React, { useEffect, useState } from 'react';
import { useSpc } from '../../context/SpcContext';
import { Reveal, Pop } from '../ui';
import { Trophy, Medal, Crown, Flame, Footprints, EyeOff, ChevronDown, Plus, Pencil, Check, X, Trash2, Gift } from 'lucide-react';
import { InfoTip } from '../InfoTip';
import { ImageGallery } from '../ImageGallery';

const POINT_RULE_FALLBACK = "This is a custom club rule — ask an admin how to qualify for it.";

const BOARDS = [
  { key: 'points', label: 'SPC Points', Icon: Trophy },
  { key: 'challenge', label: 'Challenge', Icon: Flame },
  { key: 'sessions', label: 'Sessions', Icon: Footprints },
];

function initials(name) { return (name || 'S').split(' ').filter(Boolean).map(x => x[0]).slice(0, 2).join('').toUpperCase(); }

function LbAvatar({ row, size = 40 }) {
  return (
    <div className="lb-avatar" style={{ width: size, height: size }}>
      {row.avatar ? <img src={row.avatar} alt={row.name} /> : <span style={{ fontSize: size * 0.34 }}>{row.anonymous ? '?' : initials(row.name)}</span>}
    </div>
  );
}

function formatValue(key, value, target) {
  if (key === 'points') return `${(value || 0).toLocaleString()} pts`;
  if (key === 'challenge') return `${value}/${target || 0}`;
  return `${value} ${value === 1 ? 'session' : 'sessions'}`;
}

const MEDAL = { 1: '#ffd24a', 2: '#cfd3d8', 3: '#d68a4e' };

function Podium({ rows, boardKey, target }) {
  const top = rows.slice(0, 3);
  const order = [top[1], top[0], top[2]].filter(Boolean); // 2nd, 1st, 3rd
  return (
    <div className="lb-podium" data-testid="leaderboard-podium">
      {order.map((r) => (
        <div key={r.rank} className={`lb-pod lb-pod-${r.rank} ${r.isMe ? 'me' : ''}`} data-testid={`podium-${r.rank}`}>
          <div className="lb-pod-medal" style={{ color: MEDAL[r.rank] }}>{r.rank === 1 ? <Crown size={20} /> : <Medal size={18} />}</div>
          <LbAvatar row={r} size={r.rank === 1 ? 66 : 54} />
          <div className="lb-pod-name">{r.name}{r.isMe && <span className="lb-you">YOU</span>}</div>
          <div className="lb-pod-val">{formatValue(boardKey, r.value, target)}</div>
          <div className="lb-pod-block" style={{ borderColor: MEDAL[r.rank] }}><span>{r.rank}</span></div>
        </div>
      ))}
    </div>
  );
}

function AddPointRule({ addPointRule }) {
  const [open, setOpen] = React.useState(false);
  const [label, setLabel] = React.useState('');
  const [pts, setPts] = React.useState('');
  const [explain, setExplain] = React.useState('');
  if (!open) {
    return (
      <button className="btn secondary" style={{ marginTop: 12 }} onClick={() => setOpen(true)} data-testid="add-point-rule-btn">
        <Plus size={14} /> Add a point rule
      </button>
    );
  }
  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim() || !pts) return;
    const ok = await addPointRule({ label: label.trim(), points: Number(pts), explain: explain.trim() });
    if (ok) { setLabel(''); setPts(''); setExplain(''); setOpen(false); }
  };
  return (
    <form onSubmit={submit} className="add-point-rule" data-testid="add-point-rule-form">
      <input placeholder="e.g. Turn up to a Track Night" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="apr-label" />
      <input type="number" placeholder="+pts" value={pts} onChange={(e) => setPts(e.target.value)} data-testid="apr-points" style={{ width: 96 }} />
      <input placeholder="How members actually do this (shown in the info bubble)" value={explain} onChange={(e) => setExplain(e.target.value)} data-testid="apr-explain" style={{ width: '100%', marginTop: 8 }} />
      <button type="submit" className="btn" data-testid="apr-submit">Add</button>
      <button type="button" className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
    </form>
  );
}

function PointRuleRow({ rule, role, updatePointRule, removePointRule }) {
  const [editing, setEditing] = React.useState(false);
  const [label, setLabel] = React.useState(rule.label);
  const [pts, setPts] = React.useState(rule.points);
  const [explain, setExplain] = React.useState(rule.explain || '');
  React.useEffect(() => { setLabel(rule.label); setPts(rule.points); setExplain(rule.explain || ''); }, [rule.label, rule.points, rule.explain]);
  const save = async () => {
    if (!label.trim()) return;
    const ok = await updatePointRule(rule.id, { label: label.trim(), points: Number(pts), explain: explain.trim() });
    if (ok) setEditing(false);
  };
  const cancel = () => { setLabel(rule.label); setPts(rule.points); setExplain(rule.explain || ''); setEditing(false); };

  if (editing && role === 'admin') {
    return (
      <div className="item point-rule-editing" data-testid={`edit-rule-${rule.id}`} style={{ flexWrap: 'wrap' }}>
        <input className="pr-edit-label" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="pr-edit-label" />
        <input className="pr-edit-pts" type="number" value={pts} onChange={(e) => setPts(e.target.value)} data-testid="pr-edit-pts" />
        <input placeholder="How members actually do this (shown in the info bubble)" value={explain} onChange={(e) => setExplain(e.target.value)} data-testid="pr-edit-explain" style={{ width: '100%', marginTop: 6 }} />
        <button className="btn" style={{ padding: '6px 10px' }} onClick={save} title="Save"><Check size={14} /></button>
        <button className="btn ghost" style={{ padding: '6px 10px' }} onClick={cancel} title="Cancel"><X size={14} /></button>
      </div>
    );
  }
  return (
    <div className="item" data-testid={`rule-${rule.id || rule.label}`}>
      <div className="item-main">{rule.label}<InfoTip text={rule.explain || POINT_RULE_FALLBACK} testId={`rule-info-${rule.id || rule.label}`} /></div>
      <span className="pill yellow">+{rule.points}</span>
      {role === 'admin' && rule.id && (
        <>
          <button className="btn secondary" style={{ padding: '6px 10px' }} onClick={() => setEditing(true)} title="Edit rule" data-testid={`edit-btn-${rule.id}`}>
            <Pencil size={13} />
          </button>
          <button className="btn danger" style={{ padding: '6px 10px' }} onClick={() => removePointRule(rule.id)} title="Remove rule" data-testid={`remove-rule-${rule.id}`}>
            <Trash2 size={13} />
          </button>
        </>
      )}
    </div>
  );
}

function CrownChampionPanel({ topRow, crownChampion }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  if (!open) {
    return (
      <button className="btn danger" data-testid="crown-champion-open-btn" onClick={() => setOpen(true)} style={{ margin: '12px 4px 4px' }}>
        <Crown size={14} /> Crown this year's champion
      </button>
    );
  }
  const confirm = async () => {
    setBusy(true);
    const ok = await crownChampion(label.trim());
    setBusy(false);
    if (ok) { setOpen(false); setLabel(''); }
  };
  return (
    <div className="card" data-testid="crown-champion-confirm" style={{ border: '1px solid var(--spc-yellow)', margin: '12px 4px 4px' }}>
      <p style={{ marginTop: 0 }}>
        This will crown <strong>{topRow ? topRow.name : 'the current #1'}</strong>{topRow ? ` (${topRow.value} pts)` : ''} as Club Champion
        and reset <strong>everyone's SPC Points to 0</strong>. Lifetime points are never affected, and this can be undone
        from the admin audit log if it's a mistake.
      </p>
      <input placeholder="Season label (optional, e.g. 2026)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="crown-champion-label" style={{ width: '100%' }} />
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="btn danger" disabled={busy} onClick={confirm} data-testid="crown-champion-confirm-btn">{busy ? 'Crowning…' : "Yes, crown & reset points"}</button>
        <button className="btn ghost" onClick={() => setOpen(false)} data-testid="crown-champion-cancel-btn">Cancel</button>
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const { getLeaderboard, getHallOfFame, crownChampion, toast, state, user, role, copy, addPointRule, updatePointRule, removePointRule, isAchievementUnlocked, openModal, modal, removeReward, redeemReward, getBenefits } = useSpc();
  const [data, setData] = useState(null);
  const [hallOfFame, setHallOfFame] = useState(null);
  const [board, setBoard] = useState('points');
  const [loading, setLoading] = useState(true);
  const [listOpen, setListOpen] = useState(false);
  const [pointsOpen, setPointsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rewardPhotos, setRewardPhotos] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try { const res = await getLeaderboard(); if (alive) setData(res.data); }
      catch (e) { if (alive) toast('Could not load the leaderboard'); }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [getLeaderboard, toast]);

  const fetchHallOfFame = React.useCallback(async () => {
    try { const { data } = await getHallOfFame(); setHallOfFame(data.champions || []); }
    catch (e) { /* hall of fame is a lazy enhancement */ }
  }, [getHallOfFame]);
  useEffect(() => { fetchHallOfFame(); }, [fetchHallOfFame]);

  const crownAndRefresh = async (label) => {
    const ok = await crownChampion(label);
    if (ok) {
      fetchHallOfFame();
      try { const res = await getLeaderboard(); setData(res.data); } catch (e) { /* refresh best-effort */ }
    }
    return ok;
  };

  const fetchRewardPhotos = React.useCallback(async () => {
    try {
      const { data } = await getBenefits();
      setRewardPhotos(Object.fromEntries((data.rewards || []).map(x => [x.id, { photos: x.photos || [] }])));
    } catch (e) { /* photo gallery is a lazy enhancement */ }
  }, [getBenefits]);
  useEffect(() => { fetchRewardPhotos(); }, [fetchRewardPhotos]);
  const prevModalType = React.useRef(null);
  useEffect(() => {
    if (modal) { prevModalType.current = modal.type; return; }
    if (prevModalType.current === 'newReward') fetchRewardPhotos();
    prevModalType.current = null;
  }, [modal, fetchRewardPhotos]);
  const rewardsReady = rewardPhotos !== null;
  const prizes = (state.rewards || []).filter(r => r.active).map(r => {
    const extra = rewardPhotos?.[r.id];
    return extra ? { ...r, photos: extra.photos.length ? extra.photos : r.photos } : r;
  });

  const rows = data?.boards?.[board] || [];
  const target = data?.challengeTarget;
  const myRank = data?.myRanks?.[board];
  const lifetimeRows = (data?.boards?.lifetime || []).slice(0, 5);
  const topPointsRow = (data?.boards?.points || [])[0];
  const referralPointsEarned = (state.achievementCatalogue || [])
    .filter(a => a.metric === 'referrals' && isAchievementUnlocked(a.id))
    .reduce((sum, a) => sum + Number(a.points || 0), 0);
  const myLedger = (state.adminData?.pointLedger || []).filter(e => e.userId === user?.id);

  return (
    <div data-testid="leaderboard-view">
      <Reveal className="section-head">
        <div><div className="eyebrow">Community</div><h2>SPC Points &amp; Leaderboard</h2><p>Your points, prizes, referrals, and a friendly bit of club rivalry — for the fun of it.</p></div>
      </Reveal>

      <div className="section-head"><div><div className="eyebrow">Community</div><h2>SPC Leaderboard</h2><p>A friendly bit of club rivalry — points, challenges and sessions.</p></div></div>

      <div className="seg lb-tabs" data-testid="leaderboard-tabs">
        {BOARDS.map(b => (
          <button key={b.key} className={board === b.key ? 'active' : ''} data-testid={`lb-tab-${b.key}`} onClick={() => setBoard(b.key)}>
            <b.Icon size={15} /> {b.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="empty">Loading the club rankings…</div>
      ) : rows.length === 0 ? (
        <div className="empty">No one's on the board yet — get out there!</div>
      ) : (
        <>
          {data?.optedOut && (
            <div className="notice soft" data-testid="lb-opted-out" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <EyeOff size={16} /> You're hidden from the leaderboard. You can rejoin from <strong>My SPC → Leaderboard settings</strong>.
            </div>
          )}
          <Reveal delay={0.04}><Podium rows={rows} boardKey={board} target={target} /></Reveal>

          {rows.length > 0 && (
            <>
              <button
                type="button"
                className="btn secondary lb-expand-btn"
                onClick={() => setListOpen(v => !v)}
                data-testid="leaderboard-expand-toggle"
                style={{ display: 'flex', alignItems: 'center', gap: 7, margin: '0 auto' }}
              >
                {listOpen ? 'Hide full leaderboard' : `Show full leaderboard (${rows.length})`}
                <ChevronDown size={16} className={`points-earned-chevron ${listOpen ? 'open' : ''}`} />
              </button>
              {listOpen && (
                <div className="card lb-list" style={{ marginTop: 8 }} data-testid="leaderboard-full-list">
                  {rows.map((r, i) => (
                    <Pop key={r.rank} delay={Math.min(i * 0.03, 0.3)} className={`lb-row ${r.isMe ? 'me' : ''}`} data-testid={`lb-row-${r.rank}`}>
                      <div className="lb-rank">{r.rank}</div>
                      <LbAvatar row={r} size={38} />
                      <div className="lb-name">{r.name}{r.isMe && <span className="lb-you">YOU</span>}</div>
                      <div className="lb-val">{formatValue(board, r.value, target)}</div>
                    </Pop>
                  ))}
                </div>
              )}
            </>
          )}

          {myRank && myRank.rank > 3 && (
            <div className="lb-myrank" data-testid="lb-my-rank">
              <Trophy size={16} /> You're <strong>#{myRank.rank}</strong> of {myRank.total} — {formatValue(board, myRank.value, target)}
            </div>
          )}
        </>
      )}

      <div className="section-head"><div><div className="eyebrow">All-time</div><h2>All-Time Legends</h2><p>Lifetime points never reset — this is the club's permanent top 5, season after season.</p></div></div>
      <div className="card lb-list" data-testid="lifetime-leaderboard">
        {lifetimeRows.length ? lifetimeRows.map((r) => (
          <div className={`lb-row ${r.isMe ? 'me' : ''}`} key={r.rank} data-testid={`lifetime-row-${r.rank}`}>
            <div className="lb-rank">{r.rank}</div>
            <LbAvatar row={r} size={34} />
            <div className="lb-name">{r.name}{r.isMe && <span className="lb-you">YOU</span>}</div>
            <div className="lb-val">{(r.value || 0).toLocaleString()} pts</div>
          </div>
        )) : <div className="empty" data-testid="lifetime-leaderboard-empty">No lifetime points logged yet.</div>}
      </div>

      <div className="section-head champion-head">
        <div><div className="eyebrow">Hall of Fame</div><h2><Crown size={22} style={{ verticalAlign: '-3px', marginRight: 8, color: 'var(--spc-yellow)' }} />Club Champions</h2><p>Crowned once a season — current SPC Points reset to zero, lifetime points carry on forever.</p></div>
      </div>
      {role === 'admin' && <CrownChampionPanel topRow={topPointsRow} crownChampion={crownAndRefresh} />}
      <div className="grid" data-testid="hall-of-fame-list">
        {hallOfFame === null ? (
          <div className="card span-12"><div className="empty">Loading the Hall of Fame…</div></div>
        ) : hallOfFame.length ? hallOfFame.map((c) => (
          <div className="card span-12 champion-card" key={c.id} data-testid={`champion-card-${c.id}`}>
            <Crown size={28} className="champion-icon" />
            <div className="champion-main">
              <div className="eyebrow">{c.label} Champion</div>
              <h3>{c.podium[0]?.name}</h3>
              <p>{c.podium[0]?.points?.toLocaleString()} SPC points</p>
            </div>
            {c.podium.slice(1).length > 0 && (
              <div className="champion-runners">
                {c.podium.slice(1).map((p, i) => (
                  <div key={p.memberId} className="champion-runner">
                    <span>{i === 0 ? '2nd' : '3rd'}</span>
                    <strong>{p.name}</strong>
                    <span>{p.points?.toLocaleString()} pts</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )) : (
          <div className="card span-12"><div className="empty" data-testid="hall-of-fame-empty">No champion crowned yet — the first one will make history.</div></div>
        )}
      </div>

      <div className="section-head"><div><h2>SPC Points</h2><p>Earn points by getting involved, then spend them on real prizes below.</p></div></div>
      <div className="grid">
        <div className="card span-4">
          <span className="pill yellow">Your balance</span>
          <div className="stat" style={{ marginTop: 10 }}><strong>{state.member.points}</strong><span>SPC points</span></div>
          <p>Earn points by turning up, completing challenges, joining club activity and referring new members.</p>
        </div>
        <div className="card span-8 points-earned-card">
          <button type="button" className="points-earned-toggle" onClick={() => setPointsOpen(v => !v)} data-testid="points-earned-toggle">
            <h3>How points are earned</h3>
            <ChevronDown size={18} className={`points-earned-chevron ${pointsOpen ? 'open' : ''}`} />
          </button>
          {pointsOpen && (
            <>
              {state.settings?.pointRulesNote && (
                <div className="notice" data-testid="point-rules-note" style={{ marginTop: 8 }}>{state.settings.pointRulesNote}</div>
              )}
              <div className="list" data-testid="point-rules-list" style={{ marginTop: 10 }}>
                {state.pointRules.length ? state.pointRules.map(r => (
                  <PointRuleRow key={r.id || r.label} rule={r} role={role} updatePointRule={updatePointRule} removePointRule={removePointRule} />
                )) : <div className="empty" data-testid="point-rules-empty">Point rules are being set up — check back soon.</div>}
              </div>
              {role === 'admin' && <AddPointRule addPointRule={addPointRule} />}
            </>
          )}
        </div>
      </div>

      <div className="grid">
        <div className="card span-12 points-earned-card">
          <button type="button" className="points-earned-toggle" onClick={() => setHistoryOpen(v => !v)} data-testid="points-history-toggle">
            <h3>How your points were earned</h3>
            <ChevronDown size={18} className={`points-earned-chevron ${historyOpen ? 'open' : ''}`} />
          </button>
          {historyOpen && (
            <div className="list" data-testid="points-history-list" style={{ marginTop: 10 }}>
              {myLedger.length ? myLedger.map(e => (
                <div className="item" key={e.id}>
                  <div><strong>{e.reason}</strong><div className="meta">{e.date}</div></div>
                  <span className={`pill ${e.delta >= 0 ? 'green' : 'red'}`}>{e.delta >= 0 ? '+' : ''}{e.delta} pts</span>
                </div>
              )) : <div className="empty" data-testid="points-history-empty">No points activity yet — get involved to start earning.</div>}
            </div>
          )}
        </div>
      </div>

      <div className="section-head prizes-head">
        <div><div className="eyebrow">Unlock prizes</div><h2><Gift size={22} style={{ verticalAlign: '-3px', marginRight: 8, color: 'var(--spc-yellow)' }} />Prizes</h2><p>Reach the points threshold to redeem a prize — it won't cost you any points.</p></div>
        {role === 'admin' && <button className="btn" data-testid="add-reward-btn" onClick={() => openModal('newReward')}>+ Prize</button>}
      </div>
      <div className="grid">
        {prizes.length ? prizes.map(r => (
          <div className="card span-4 prize-card" key={r.id} data-testid={`prize-card-${r.id}`}>
            {r.photos && r.photos.length > 0 && <ImageGallery photos={r.photos} alt={r.title} testId={`reward-gallery-${r.id}`} />}
            <span className="pill yellow">{r.cost} pts</span>
            <h3 style={{ marginTop: 11 }}>{r.title}</h3>
            <p>{r.description}</p>
            {role === 'admin'
              ? <div style={{ display: 'flex', gap: 8, marginTop: 12 }}><button className="btn secondary" disabled={!rewardsReady} title={!rewardsReady ? 'Loading photos…' : ''} onClick={() => openModal('newReward', r)} data-testid={`edit-reward-${r.id}`}><Pencil size={13} /> {rewardsReady ? 'Edit' : 'Loading…'}</button><button className="btn danger" onClick={() => removeReward(r.id)} data-testid={`remove-reward-${r.id}`}>Remove</button></div>
              : <button className={`btn ${state.member.points >= r.cost ? '' : 'secondary'}`} style={{ marginTop: 12 }} disabled={state.member.points < r.cost} onClick={() => redeemReward(r.id)} data-testid={`redeem-reward-${r.id}`}>{state.member.points >= r.cost ? 'Redeem' : 'Need ' + (r.cost - state.member.points) + ' more'}</button>}
          </div>
        )) : <div className="card span-12"><div className="empty" data-testid="prizes-empty">No prizes live yet — check back soon.</div></div>}
      </div>

      <div className="section-head"><div><h2>Member referral rewards</h2><p>Bring someone good into SPC and both sides can benefit.</p></div></div>
      <div className="grid">
        <div className="card span-5">
          <span className="pill yellow">Your referral code<InfoTip text="Share this code with a friend when they sign up. They paste it into the 'Referral code' field on the Create Account screen. Once they become an active paying member, you automatically earn the referral points above." testId="referral-info" /></span>
          <h3 style={{ marginTop: 12 }} className="discount-code">{state.member.referralCode}</h3>
          <p>Share this code with someone joining SPC. A referral becomes successful once the new member meets the qualifying rule set by SPC.</p>
          {role === 'member' && <button className="btn secondary" onClick={() => copy(state.member.referralCode, 'Referral code copied')}>Copy referral code</button>}
        </div>
        <div className="card span-7">
          <div className="stat"><div><span>Successful referrals</span><br /><strong>{state.member.successfulReferrals}</strong></div><span className="pill yellow" data-testid="referral-pts-earned">+{referralPointsEarned} pts earned</span></div>
          <div className="list" style={{ marginTop: 14 }} data-testid="referrals-list">
            {state.referrals.length ? state.referrals.map(r => (
              <div className="item" key={r.id} data-testid={`referral-row-${r.id}`}>
                <div><strong>{r.name}</strong><div className="meta">Joined {r.joinedAt}</div></div>
                <span className={`pill ${r.status === 'joined' ? 'green' : ''}`}>{r.status === 'joined' ? 'Active member' : 'Pending payment'}</span>
              </div>
            )) : <div className="empty" data-testid="referrals-empty">No referrals yet — share your code to get started.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
